/**
 * @file PickingTaskMeta.tsx
 * @description Плитки шапки задания на сборку (phase19.23: экран сборки
 * приведён к эталону складского документа).
 *
 * Те же четыре плитки, что у приёмки и списания, только поля свои: заказ,
 * исполнитель, начало сборки и прогресс. `buildDocumentMeta` не подходит —
 * у задания нет ни автора, ни «проведено», ни цены строк, — но сетка и формат
 * даты берутся общие (`DocumentMetaGrid`, `formatDocDateTime`).
 */

import { Link } from "react-router";

import { countShortageItems } from "./picking-actions-state";
import { PickedTileValue } from "./PickedTileValue";
import type { DocumentMetaItem } from "../../ui/DocumentMetaGrid";
import { formatDocDateTime } from "../wms-document-meta";
import type { PickingTaskDetailDto } from "../../../lib/api-picking";

interface MetaInput {
  task: PickingTaskDetailDto;
  /** Текущий пользователь: своё задание подписываем «вы». */
  currentUserId: string | null;
  /** Слаг магазина для ссылки на заказ. */
  slug: string | undefined;
  /** Право SHOP_VIEW_ORDERS: без него номер заказа — просто текст. */
  canOpenOrder: boolean;
}

/** Четыре плитки задания: заказ, исполнитель, начало сборки, прогресс. */
export function buildPickingTaskMeta({
  task,
  currentUserId,
  slug,
  canOpenOrder,
}: MetaInput): DocumentMetaItem[] {
  const mine = task.assignee?.id === currentUserId;
  const assignee = task.assignee
    ? mine
      ? "вы"
      : (task.assignee.displayName ?? "другой сотрудник")
    : "не взято";
  // Началом сборки считаем момент, когда задание взяли; до этого — создание.
  const startedAt = task.startedAt ?? task.createdAt;
  const done = task.pickedCount >= task.itemsCount && task.itemsCount > 0;

  return [
    {
      label: "Заказ",
      value:
        canOpenOrder && slug ? (
          <Link
            to={`/shop/${slug}/orders/${task.orderId}`}
            className="text-primary underline underline-offset-2"
          >
            {task.orderNumber}
          </Link>
        ) : (
          task.orderNumber
        ),
      mono: true,
    },
    {
      label: "Исполнитель",
      value: assignee,
      tone: task.assignee ? "default" : "warning",
    },
    {
      label: task.startedAt ? "На сборке с" : "Создано",
      value: formatDocDateTime(startedAt),
      mono: true,
    },
    {
      label: "Собрано",
      value: (
        <PickedTileValue
          picked={task.pickedCount}
          total={task.itemsCount}
          shortageCount={countShortageItems(task)}
        />
      ),
      mono: true,
      tone: done ? "success" : "default",
    },
  ];
}
